import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatPhone } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';
import { AttachmentChips } from '../components/Attachments.js';

/**
 * Conversations.
 *
 * The point of this screen — and the reason the data was worth the effort to recover — is that
 * SMS, email and internal notes sit in **one** thread per customer. The old system split the
 * same conversation across channels; here a customer's history reads as a single exchange, and
 * `transport` is a small mark on each message rather than a separate inbox.
 *
 * Two lists side by side: threads on the left, the open conversation on the right. Which one
 * is open lives in the URL, so a thread can be linked to and a reload comes back to it.
 */

type ThreadRow = {
  id: string;
  kind: string;
  subject: string | null;
  /** Pre-resolved label: the customer, the channel name, or whoever wrote last. */
  title: string;
  /** A job-note thread with no customer. 1758 of them — a third of the inbox. */
  internal: boolean;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  awaitingReply: boolean;
  customer: { id: string; name: string } | null;
};

type Message = {
  id: string;
  authorKind: 'staff' | 'customer' | 'system';
  authorName: string | null;
  transport: string;
  body: string | null;
  sentAt: string;
  readAt: string | null;
  failedReason: string | null;
  attachments: Array<{
    id: string;
    fileName: string;
    contentType: string;
    byteSize: number;
    caption: string | null;
    /**
     * Explicitly `null` when the file was never downloaded — then it is named, not linked.
     * Absent altogether when the conversation endpoint simply does not build one, in which
     * case the viewer composes it from the attachment's id, the same way every other
     * business URL in this application is composed.
     */
    url?: string | null;
  }>;
};

const TRANSPORT_LABEL: Record<string, string> = {
  sms: 'SMS',
  email: 'Courriel',
  in_app: 'Application',
  note: 'Note interne',
  call: 'Appel',
};

export function InboxScreen() {
  const { thread: openThreadId, filter, q } = useSearch({ from: '/inbox' });
  const navigate = useNavigate();

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }), replace: true });

  const threads = useQuery({
    queryKey: ['threads', filter, q ?? null],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '60', filter });
      if (q) params.set('q', q);
      return request<{ items: ThreadRow[]; total: number; awaitingCount: number; internalCount: number }>(
        `/threads?${params}`,
      );
    },
    placeholderData: (prev) => prev,
  });

  const conversation = useQuery({
    queryKey: ['messages', openThreadId],
    queryFn: () =>
      request<{
        thread: { id: string; subject: string | null; messageCount: number; customer: { id: string; name: string; phone: string | null } | null };
        messages: Message[];
      }>(`/threads/${openThreadId}`),
    enabled: Boolean(openThreadId),
  });

  const rows = threads.data?.items ?? [];

  return (
    <div className="inbox">
      <aside className="inbox__list" aria-label="Conversations">
        <header className="inbox__listHead">
          <input
            value={q ?? ''}
            onChange={(e) => setSearch({ q: e.target.value || undefined })}
            placeholder="Client, message…"
            aria-label="Rechercher dans les conversations"
            className="inbox__search"
          />
          <div className="inbox__filters" role="group" aria-label="Filtres">
            {(
              [
                ['all', 'Toutes'],
                // The count is the useful part: how many customers are waiting on us. It
                // counts customer messages only — an internal note is not an unanswered
                // question from outside.
                ['unread', `En attente${threads.data?.awaitingCount ? ` (${threads.data.awaitingCount})` : ''}`],
                ['customer', 'Clients'],
                ['internal', `Notes${threads.data?.internalCount ? ` (${threads.data.internalCount})` : ''}`],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`inbox__filter${filter === value ? ' inbox__filter--on' : ''}`}
                aria-pressed={filter === value}
                onClick={() => setSearch({ filter: value, thread: undefined })}
              >
                {label}
              </button>
            ))}
          </div>
        </header>

        {threads.isLoading && <p className="screen__loading">Chargement…</p>}
        {!threads.isLoading && rows.length === 0 && (
          <p className="section__empty">Aucune conversation ne correspond.</p>
        )}

        <ol className="inbox__threads">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className={`inbox__thread${openThreadId === row.id ? ' inbox__thread--open' : ''}`}
                onClick={() => setSearch({ thread: row.id })}
                aria-current={openThreadId === row.id ? 'true' : undefined}
              >
                <span className="inbox__threadTop">
                  <span className="inbox__threadName">
                    {row.internal && (
                      <span className="inbox__internalMark" title="Note interne — jamais envoyée au client">
                        Note
                      </span>
                    )}
                    {row.title}
                  </span>
                  {row.lastMessageAt && (
                    <span className="inbox__threadWhen">{shortDate(row.lastMessageAt)}</span>
                  )}
                </span>
                <span className="inbox__threadPreview">{row.lastMessagePreview ?? '—'}</span>
                {/* The customer spoke last: this thread is waiting on us. Not a read receipt —
                    somebody glancing at it does not answer it. */}
                {row.awaitingReply && <span className="inbox__awaiting">En attente de réponse</span>}
              </button>
            </li>
          ))}
        </ol>
      </aside>

      <section className="inbox__conversation" aria-label="Conversation">
        {!openThreadId && (
          <div className="empty">
            <span className="empty__title">Choisissez une conversation</span>
            <p className="empty__body">
              Les SMS, courriels et notes internes d’un client apparaissent ici dans un seul fil.
            </p>
          </div>
        )}

        {openThreadId && conversation.isLoading && <p className="screen__loading">Chargement…</p>}

        {openThreadId && conversation.data && (
          <>
            <header className="inbox__convHead">
              <div>
                {conversation.data.thread.customer ? (
                  <RecordLink kind="customer" id={conversation.data.thread.customer.id} className="inbox__convName">
                    {conversation.data.thread.customer.name}
                  </RecordLink>
                ) : (
                  <span className="inbox__convName">{conversation.data.thread.subject ?? 'Conversation'}</span>
                )}
                <span className="inbox__convCount">{conversation.data.thread.messageCount} messages</span>
              </div>
              {conversation.data.thread.customer?.phone && (
                <a className="inbox__convCall phone" href={`tel:${conversation.data.thread.customer.phone.replace(/\D/g, '')}`}>
                  {formatPhone(conversation.data.thread.customer.phone)}
                </a>
              )}
            </header>

            {conversation.data.messages.length === 0 && (
              <div className="empty">
                <span className="empty__title">Aucun message dans ce fil</span>
                <p className="empty__body">
                  La conversation existe mais son contenu n’a pas encore été importé.
                </p>
              </div>
            )}

            <ol className="inbox__messages">
              {conversation.data.messages.map((message) => (
                <li
                  key={message.id}
                  className={`msg msg--${message.authorKind}${message.transport === 'note' ? ' msg--note' : ''}`}
                >
                  <div className="msg__head">
                    <span className="msg__author">
                      {message.authorKind === 'customer' ? conversation.data!.thread.customer?.name ?? 'Client' : message.authorName ?? 'Nous'}
                    </span>
                    <span className="msg__transport">{TRANSPORT_LABEL[message.transport] ?? message.transport}</span>
                    <span className="msg__when">{longDate(message.sentAt)}</span>
                  </div>
                  <p className="msg__body">{message.body ?? <em>(sans texte)</em>}</p>

                  {/* Opened in the viewer rather than in a new tab. A customer's photograph
                      of a leaking unit and the PDF a supplier sent back are the same gesture
                      from here, and neither should cost the reader their place in the
                      conversation. */}
                  <AttachmentChips items={message.attachments} />

                  {message.failedReason && (
                    <p className="msg__failed">Non livré : {message.failedReason}</p>
                  )}
                </li>
              ))}
            </ol>

            {/* Composing is not wired up: sending goes through the outbound guard, which is
                blocked in development, and a reply box that silently does nothing would be
                worse than none. */}
            <footer className="inbox__composer">
              <p>
                L’envoi de messages est désactivé en développement — aucun message ne peut
                atteindre un client réel.
              </p>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
  }
  if (date.getFullYear() === today.getFullYear()) {
    return date.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' });
  }
  return date.toLocaleDateString('fr-CA', { month: 'short', year: 'numeric' });
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
